// src/features/s-curve/api/sCurve.api.ts
import api from '../../../shared/api/axios';

export type SCurveSource = { type: 'CURRENT' } | { type: 'DRAFT' } | { type: 'VERSION'; versionId: string };

export type SCurveTimeUnit = 'DAY' | 'WEEK';
export type SCurveAllocationMethod = 'LINEAR' | 'MILESTONE' | 'INTERNAL_CURVE';

export type SCurveRow = {
    date: string;
    label: string;

    pvInc: number;
    pvCum: number;

    pvForecastInc?: number | null;
    pvForecastCum?: number | null;

    evForecastInc?: number | null;
    evForecastCum?: number | null;

    evInc: number;
    evCum: number;
    spi: number | null;

    evApprovedInc: number;
    evApprovedCum: number;
    spiApproved: number | null;

    evLatestInc: number;
    evLatestCum: number;
    spiLatest: number | null;

    evDeltaCum: number;
};

export type DraftPatchRow = {
    key: string;
    nodeId: string | null;
    plannedStart: string | null;
    plannedFinish: string | null;
    weight: number | null; // 0~100
};

export type ForecastModel = 'LEGACY' | 'EVM_SPI_TIME_EAC';

export type RunSCurveReq = {
    source: SCurveSource;
    timeUnit: SCurveTimeUnit;
    allocationMethod: SCurveAllocationMethod;
    forecastModel: ForecastModel | null;
    draftPatch?: DraftPatchRow[];
};

export type ForecastBaseline = {
    t0WeekOf: string | null;
    t0Index: number;
    e0Cum: number;
    planEndWeekOf: string | null;
    planEndIndex: number;
};

export type EtaMeta = {
    eta: number | null;
    windowWeeks: number;
    range: { from: number; to: number } | null;
    excludedWeeks: number;
};

export type ForecastSummary = {
    case: 'NO_DATA' | 'EARLY' | 'ON_TIME' | 'DELAY' | 'DELAY_UNKNOWN';
    pEnd: number | null;
    forecastEndWeekOf: string | null;
    deltaWeeks: number | null;
    deltaDays: number | null;
    extraWeeks: number | null;
    tailWeeklyInc: number | null;
};

export type RunSCurveMeta = {
    source: 'CURRENT' | 'DRAFT' | 'VERSION';
    version: string | null;
    timeUnit: SCurveTimeUnit;
    allocationMethod: SCurveAllocationMethod;
    leafCount: number;
    warnings: string[];

    invalidCount?: number;

    forecastBaseline: ForecastBaseline;
    eta: number | null;
    etaMeta: EtaMeta;
    forecastSummary: ForecastSummary;
};

export type RunSCurveRes = {
    ok: true;
    stage: number;
    meta: RunSCurveMeta;
    rows: SCurveRow[];
};

export type FitSCurveReq = {
    source: SCurveSource;
    timeUnit: SCurveTimeUnit;
    allocationMethod: SCurveAllocationMethod;

    target: 'PV_CUM';

    alphaRange: { min: number; max: number };
    betaRange: { min: number; max: number };
    step: number;
};

export type FitSCurveRes = {
    alpha: number;
    beta: number;
    score: number;
};

export async function apiRunSCurve(projectId: string, body: RunSCurveReq): Promise<RunSCurveRes> {
    const res = await api.post(`projects/${projectId}/s-curve/run`, body);
    return res.data;
}

export async function apiFitSCurve(projectId: string, body: FitSCurveReq): Promise<FitSCurveRes> {
    const res = await api.post(`projects/${projectId}/s-curve/fit`, body);
    return res.data;
}

// ✅ Project Settings(프로젝트 기본값) 로드/저장
export async function apiGetSCurveBenchmark(projectId: string): Promise<{ alpha: number | null; beta: number | null }> {
    const res = await api.get(`/projects/${projectId}/settings`);
    const s = res.data?.data ?? res.data?.setting ?? res.data;
    return {
        alpha: s?.sCurveFactorAlpha ?? null,
        beta: s?.sCurveFactorBeta ?? null,
    };
}

export async function apiSaveSCurveBenchmark(projectId: string, alpha: number, beta: number) {
    const res = await api.put(`/projects/${projectId}/settings/s-curve`, {
        sCurveFactorAlpha: alpha,
        sCurveFactorBeta: beta,
    });
    return res.data;
}
